import { processEnergyDataWithExtrapolation, ExtrapolationResult } from '../../services/energyExtrapolationService';

export interface EnergySeriesResult {
  chartData: any;
  chartOptions: any;
  latestYear?: number;
  latestValue?: number;
  usedDataSources?: string[]; // List of unique data sources used (for methodology section)
}

type Sector = 'residential' | 'commercial';

// Sector-specific color shades (dark=observed, medium=interpolated, light=extrapolated)
const sectorColors: Record<Sector, { observed: string; interpolated: string; extrapolated: string }> = {
  residential: {
    observed: '#1e40af',      // Dark blue
    interpolated: '#3b82f6',  // Medium blue
    extrapolated: '#60a5fa'   // Light blue
  },
  commercial: {
    observed: '#166534',      // Dark green
    interpolated: '#22c55e',  // Medium green
    extrapolated: '#4ade80'   // Light green
  }
};

const commonOptions = (title: string, yLabel: string, stacked = false) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { 
      position: 'bottom',
      labels: {
        padding: 10,
        usePointStyle: true,
        pointStyle: 'rect'
      }
    },
    title: { display: true, text: title },
    tooltip: {
      callbacks: {
        label: (ctx: any) => {
          const v = Number(ctx.parsed.y);
          return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toLocaleString() : v}`;
        }
      }
    }
  },
  scales: {
    x: { stacked, title: { display: true, text: 'Year' } },
    y: { stacked, beginAtZero: true, title: { display: true, text: yLabel }, ticks: { callback: (v: any) => Number(v).toLocaleString() } }
  },
  layout: {
    padding: {
      bottom: 20
    }
  }
});

export async function fetchElectricitySeries(location: string, sector: Sector): Promise<EnergySeriesResult> {
  // Use the new trend-based extrapolation service
  // Use db_elec_residential_clean for accurate residential data
  const table = sector === 'residential' ? 'db_elec_residential_clean' : 'db_elec_commercial';
  const years = Array.from({ length: 20 }, (_, i) => 2005 + i);
  
  try {
    const result: ExtrapolationResult = await processEnergyDataWithExtrapolation(
      location, 
      table, 
      'electricity', 
      years
    );
    
    // Collect unique observed data sources (for methodology)
    const usedDataSourcesSet = new Set<string>();
    
    // Group observed data by data_source to create separate datasets
    // Key: data source name (e.g., "PG&E", "eGRID"), Value: array of values per year
    const observedDataBySource = new Map<string, number[]>();
    const interpolatedData: number[] = [];
    const extrapolatedData: number[] = [];
    
    // Initialize all arrays with zeros
    result.years.forEach(() => {
      interpolatedData.push(0);
      extrapolatedData.push(0);
    });
    
    result.years.forEach((year, index) => {
      const value = result.values[index] || 0;
      const source = result.sources[index];
      const dataSource = result.dataSources?.[index];
      
      if (source === 'observed' && dataSource) {
        // For observed data, create/update dataset for this specific data source
        // Use the dataSource string as-is (may contain multiple sources like "PG&E, eGRID")
        if (!observedDataBySource.has(dataSource)) {
          // Initialize array with zeros for this data source
          observedDataBySource.set(dataSource, new Array(result.years.length).fill(0));
        }
        const dataArray = observedDataBySource.get(dataSource)!;
        dataArray[index] = value;
        // Track individual sources for methodology section
        const sources = dataSource.split(',').map(s => s.trim()).filter(Boolean);
        sources.forEach((src) => usedDataSourcesSet.add(src));
      } else if (source === 'observed' && !dataSource) {
        // Observed but no data_source specified - use generic "Observed"
        if (!observedDataBySource.has('Observed')) {
          observedDataBySource.set('Observed', new Array(result.years.length).fill(0));
        }
        const dataArray = observedDataBySource.get('Observed')!;
        dataArray[index] = value;
      } else if (source === 'interpolated') {
        interpolatedData[index] = value;
      } else if (source === 'extrapolated') {
        extrapolatedData[index] = value;
      }
    });
    
    // Create datasets array - one per observed data source, plus interpolated and extrapolated
    const datasets: any[] = [];
    
    // Add observed datasets (sorted for consistency)
    const sortedDataSources = Array.from(observedDataBySource.keys()).sort();
    sortedDataSources.forEach((dataSource) => {
      const label = dataSource === 'Observed' ? 'Observed' : `Observed - ${dataSource}`;
      datasets.push({
        type: 'bar',
        label: label,
        data: observedDataBySource.get(dataSource),
        backgroundColor: sectorColors[sector].observed,
        borderColor: '#374151',
        borderWidth: 1
      });
    });
    
    // Add interpolated dataset
    if (interpolatedData.some(v => v > 0)) {
      datasets.push({
        type: 'bar',
        label: 'Interpolated',
        data: interpolatedData,
        backgroundColor: sectorColors[sector].interpolated,
        borderColor: '#374151',
        borderWidth: 1
      });
    }
    
    // Add extrapolated dataset
    if (extrapolatedData.some(v => v > 0)) {
      datasets.push({
        type: 'bar',
        label: 'Extrapolated',
        data: extrapolatedData,
        backgroundColor: sectorColors[sector].extrapolated,
        borderColor: '#374151',
        borderWidth: 1
      });
    }
    
    const chartData = {
      labels: years,
      datasets: datasets
    };
    
    const latestYear = years.length ? years[years.length - 1] : undefined;
    const latestValue = result.values.length ? result.values[result.values.length - 1] || 0 : undefined;
    
    return {
      chartData,
      chartOptions: commonOptions(`${sector === 'residential' ? 'Residential' : 'Commercial'} Electricity Consumption (kWh)`, 'kWh', true),
      latestYear,
      latestValue,
      usedDataSources: Array.from(usedDataSourcesSet).sort()
    };
    
  } catch (error) {
    console.error(`Error fetching electricity data for ${location}:`, error);
    throw error;
  }
}

export async function fetchElectricityStacked(location: string): Promise<EnergySeriesResult> {
  const [res, com] = await Promise.all([
    fetchElectricitySeries(location, 'residential'),
    fetchElectricitySeries(location, 'commercial')
  ]);
  const labels: number[] = Array.from(new Set([...(res.chartData.labels || []), ...(com.chartData.labels || [])])).sort();

  // Create maps for each series
  const resMap = new Map((res.chartData.labels || []).map((y: number, i: number) => [y, (res.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));
  const comMap = new Map((com.chartData.labels || []).map((y: number, i: number) => [y, (com.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));

  const resSeries = labels.map((y) => (resMap.has(y) ? (resMap.get(y) as number) : 0));
  const comSeries = labels.map((y) => (comMap.has(y) ? (comMap.get(y) as number) : 0));

  return {
    chartData: {
      labels,
      datasets: [
        { type: 'bar', label: 'Residential Electricity', data: resSeries, backgroundColor: sectorColors.residential.observed },
        { type: 'bar', label: 'Commercial Electricity', data: comSeries, backgroundColor: sectorColors.commercial.observed }
      ]
    },
    chartOptions: commonOptions('Total Electricity Consumption (kWh)', 'kWh', true)
  };
}

export async function fetchNaturalGasSeries(location: string, sector: Sector): Promise<EnergySeriesResult> {
  // Use the new trend-based extrapolation service
  const table = sector === 'residential' ? 'db_ng_residential' : 'db_ng_commercial';
  const years = Array.from({ length: 20 }, (_, i) => 2005 + i);
  
  try {
    const result: ExtrapolationResult = await processEnergyDataWithExtrapolation(
      location, 
      table, 
      'natural_gas', 
      years
    );
    
    // Create datasets for stacked bars (one per provenance type)
    const observedData: number[] = [];
    const interpolatedData: number[] = [];
    const extrapolatedData: number[] = [];
    
    result.years.forEach((year, index) => {
      const value = result.values[index] || 0;
      const source = result.sources[index];
      
      // Initialize all arrays with zeros for this year
      observedData.push(0);
      interpolatedData.push(0);
      extrapolatedData.push(0);
      
      // Put the actual value in the appropriate dataset
      if (source === 'observed') {
        observedData[observedData.length - 1] = value;
      } else if (source === 'interpolated') {
        interpolatedData[interpolatedData.length - 1] = value;
      } else if (source === 'extrapolated') {
        extrapolatedData[extrapolatedData.length - 1] = value;
      }
    });
    
    const chartDataGas = {
      labels: years,
      datasets: [
        { 
          type: 'bar', 
          label: 'Observed', 
          data: observedData, 
          backgroundColor: sectorColors[sector].observed,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Interpolated', 
          data: interpolatedData, 
          backgroundColor: sectorColors[sector].interpolated,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Extrapolated', 
          data: extrapolatedData, 
          backgroundColor: sectorColors[sector].extrapolated,
          borderColor: '#374151',
          borderWidth: 1
        }
      ]
    };
    
    const latestYear = years.length ? years[years.length - 1] : undefined;
    const latestValue = result.values.length ? result.values[result.values.length - 1] || 0 : undefined;
    
    return {
      chartData: chartDataGas,
      chartOptions: commonOptions(`${sector === 'residential' ? 'Residential' : 'Commercial'} Natural Gas (therms)`, 'Therms', true),
      latestYear,
      latestValue
    };
    
  } catch (error) {
    console.error(`Error fetching natural gas data for ${location}:`, error);
    throw error;
  }
}

export async function fetchNaturalGasStacked(location: string): Promise<EnergySeriesResult> {
  const [res, com] = await Promise.all([
    fetchNaturalGasSeries(location, 'residential'),
    fetchNaturalGasSeries(location, 'commercial')
  ]);
  const labels: number[] = Array.from(new Set([...(res.chartData.labels || []), ...(com.chartData.labels || [])])).sort();

  // Create maps for each series
  const resMap = new Map((res.chartData.labels || []).map((y: number, i: number) => [y, (res.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));
  const comMap = new Map((com.chartData.labels || []).map((y: number, i: number) => [y, (com.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));

  const resSeries = labels.map((y) => (resMap.has(y) ? (resMap.get(y) as number) : 0));
  const comSeries = labels.map((y) => (comMap.has(y) ? (comMap.get(y) as number) : 0));

  return {
    chartData: {
      labels,
      datasets: [
        { type: 'bar', label: 'Residential Natural Gas', data: resSeries, backgroundColor: sectorColors.residential.observed },
        { type: 'bar', label: 'Commercial Natural Gas', data: comSeries, backgroundColor: sectorColors.commercial.observed }
      ]
    },
    chartOptions: commonOptions('Total Natural Gas (therms)', 'Therms', true)
  };
}


